// FILE: src/components/Header.jsx
// ✅ Production-ready header (complete file)
// ✅ Fix: mobile drawer now covers full width (no background header visible, no double logo feel)
// ✅ Fix: stronger z-index layering + scroll lock
// ✅ Payout chip never shows "DRAFT" → shows "SETUP REQUIRED"
// ✅ Payout fetch is lazy (only when menu opens)
// ✅ Safe click-outside + Esc close
// ✅ Mobile drawer includes payout link for owners

import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUser, ROLES } from "../context/UserContext";
import { fetchOwnerProfile, fetchOwnerPayoutAccount } from "../api/ownerPortalApi";
import { CART_CHANGED_EVENT, getCartForCurrentUser } from "../api/cartApi";

/* ---------- helpers ---------- */
const navClass = ({ isActive }) =>
  `px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
  }`;

function titleCase(s = "") {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function nameFromEmail(email = "") {
  const local = String(email).split("@")[0] || "";
  const cleaned = local.replace(/[._-]+/g, " ");
  return titleCase(cleaned).split(" ")[0] || "User";
}

function getDisplayName(user) {
  if (!user) return "Account";
  const n =
    (user.userName && titleCase(user.userName)) ||
    (user.name && titleCase(user.name)) ||
    (user.firstName &&
      user.lastName &&
      titleCase(`${user.firstName} ${user.lastName}`)) ||
    (user.firstName && titleCase(user.firstName)) ||
    (user.email && nameFromEmail(user.email)) ||
    (user.phoneNumber && user.phoneNumber) ||
    "Account";
  return n;
}

function getAvatarLetter(user) {
  if (!user) return "U";
  const source =
    (user.userName && user.userName) ||
    (user.name && user.name) ||
    user.email ||
    user.phoneNumber ||
    "U";
  const first = String(source).trim().charAt(0);
  return first ? first.toUpperCase() : "U";
}

function normStr(s = "") {
  return String(s ?? "").trim();
}

function makeTripKey(it) {
  const stops = Array.isArray(it?.destinations)
    ? it.destinations.map((s) => normStr(s)).filter(Boolean)
    : [];

  return JSON.stringify({
    pickupLocation: normStr(it?.pickupLocation),
    destinations: stops,
    destination: normStr(it?.destination || it?.finalDestination),
    pickupDate: normStr(it?.pickupDate),
    pickupTime: normStr(it?.pickupTime),
    dropDate: normStr(it?.dropDate),
    dropTime: normStr(it?.dropTime),
  });
}

function countTripPlans(items) {
  const keys = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    keys.add(makeTripKey(item));
  }
  return keys.size;
}

function safeStr(x) {
  return x == null ? "" : String(x);
}
function up(x) {
  return safeStr(x).trim().toUpperCase();
}

/* ✅ Friendly status label (never show DRAFT) */
function payoutLabel(status) {
  const s = up(status);
  if (!s || s === "DRAFT") return "SETUP REQUIRED";
  if (s === "SUBMITTED") return "SUBMITTED";
  if (s === "VERIFIED") return "VERIFIED";
  if (s === "ACTION_REQUIRED") return "ACTION REQUIRED";
  return s;
}

function statusTone(status) {
  const s = up(status);
  if (s === "VERIFIED") return "good";
  if (s === "ACTION_REQUIRED") return "bad";
  if (s === "SUBMITTED") return "warn";
  return "neutral";
}

function StatusChip({ status }) {
  const tone = statusTone(status);
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-white text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {payoutLabel(status)}
    </span>
  );
}

/* ---------- component ---------- */
export default function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { booted, user, signOut } = useUser();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [supportOpen, setSupportOpen] = React.useState(false);
  const [cartPlanCount, setCartPlanCount] = React.useState(0);
  const menuRef = React.useRef(null);
  const supportRef = React.useRef(null);

  // Detect "owner surface"
  const isOwnerSurface =
    pathname.startsWith("/owners") ||
    pathname.startsWith("/owner") ||
    pathname.startsWith("/vehicle-owner");

  const isSignedIn = !!(user?.email || user?.phoneNumber);
  const role = user?.role || ROLES.USER;
  const ownerSignedIn = role === ROLES.OWNER && isSignedIn;

  const headerUser = isSignedIn ? user : null;

  // 🔐 Track whether owner already has a password
  const [hasOwnerPassword, setHasOwnerPassword] = React.useState(null);

  // ✅ Payout account status summary (only for menu)
  const [payoutSummary, setPayoutSummary] = React.useState(null); // {status, method}
  const [payoutLoading, setPayoutLoading] = React.useState(false);

  // Close handlers
  const closeAll = React.useCallback(() => {
    setMenuOpen(false);
    setMobileOpen(false);
    setSupportOpen(false);
  }, []);

  // ✅ lock background scroll when mobile drawer open
  React.useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Owner password status
  React.useEffect(() => {
    let active = true;

    const loadOwnerPasswordStatus = async () => {
      if (!isSignedIn || role !== ROLES.OWNER) {
        if (active) setHasOwnerPassword(null);
        return;
      }

      try {
        const profile = await fetchOwnerProfile();
        if (!active) return;
        setHasOwnerPassword(
          profile && typeof profile.hasPassword === "boolean"
            ? profile.hasPassword
            : false
        );
      } catch (err) {
        console.error("Failed to fetch owner password status", err);
        if (active) setHasOwnerPassword(null);
      }
    };

    loadOwnerPasswordStatus();
    return () => {
      active = false;
    };
  }, [isSignedIn, role]);

  // ✅ Lazy-load payout status only when menu opens (owner only)
  React.useEffect(() => {
    let active = true;

    async function loadPayout() {
      if (!menuOpen) return;
      if (!isSignedIn || role !== ROLES.OWNER) return;
      if (payoutLoading) return;

      setPayoutLoading(true);
      try {
        const res = await fetchOwnerPayoutAccount();
        if (!active) return;
        const a = res?.account || res;
        setPayoutSummary(
          a
            ? { status: a.status || "DRAFT", method: a.method || "UPI" }
            : { status: "DRAFT", method: "UPI" }
        );
      } catch (e) {
        console.error("Failed to fetch payout account", e);
        if (!active) return;
        setPayoutSummary(null);
      } finally {
        if (active) setPayoutLoading(false);
      }
    }

    loadPayout();
    return () => {
      active = false;
    };
  }, [menuOpen, isSignedIn, role, payoutLoading]);

  React.useEffect(() => {
    let active = true;

    async function loadCartPlanCount() {
      if (!booted) return;
      if (!isSignedIn || role !== ROLES.USER) {
        if (active) setCartPlanCount(0);
        return;
      }

      try {
        const cart = await getCartForCurrentUser();
        if (!active) return;
        setCartPlanCount(countTripPlans(cart?.items));
      } catch (error) {
        console.error("Failed to load cart plan count", error);
        if (active) setCartPlanCount(0);
      }
    }

    loadCartPlanCount();

    function onCartChanged() {
      loadCartPlanCount();
    }

    window.addEventListener(CART_CHANGED_EVENT, onCartChanged);
    return () => {
      active = false;
      window.removeEventListener(CART_CHANGED_EVENT, onCartChanged);
    };
  }, [booted, isSignedIn, role, pathname]);

  // Public web stays user-focused.
  const signInHref = "/login";
  const signInLabel = "Sign in";

  // Logo target
  const logoHref = React.useMemo(() => {
    if (isOwnerSurface) {
      if (ownerSignedIn) return "/owner/dashboard";
      return "/owners/onboard";
    }

    if (!booted || !isSignedIn) return "/";

    switch (role) {
      case ROLES.OWNER:
        return "/owner/dashboard";
      case ROLES.DRIVER:
        return "/driver/dashboard";
      default:
        return "/user-dashboard";
    }
  }, [booted, isSignedIn, role, isOwnerSurface, ownerSignedIn]);

  // Standard navs
  const userNav = [
    { to: "/", label: "Home" },
    { to: "/discover", label: "Search" },
    { to: "/my-bookings", label: "Bookings" },
    { to: "/cart", label: "Cart" },
    { to: "/about-wayroot", label: "About" },
  ];

  const ownerNav = [
    { to: "/owners/onboard", label: "Home" },
    { to: "/owner/cars", label: "Fleet" },
    { to: "/owner/bookings", label: "Bookings" },
    { to: "/owner/drivers", label: "Drivers" },
    { to: "/owner/earnings", label: "Earnings" },
    { to: "/about-wayroot", label: "About" },
  ];

  const driverNav = [
    { to: "/", label: "Home" },
    { to: "/driver/jobs", label: "Jobs" },
    { to: "/driver/schedule", label: "Schedule" },
    { to: "/driver/earnings", label: "Earnings" },
    { to: "/about-wayroot", label: "About" },
  ];

  const roleNav =
    role === ROLES.OWNER ? ownerNav : role === ROLES.DRIVER ? driverNav : userNav;

  const activeNav = isOwnerSurface ? ownerNav : roleNav;

  const signOutNow = async () => {
    await signOut();
    try {
      localStorage.removeItem("ownerEmail");
      localStorage.removeItem("driverPhone");
    } catch {}
    closeAll();
    if (!pathname.startsWith("/discover")) navigate("/", { replace: true });
  };

  // close popovers on click-outside & Esc
  React.useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (supportRef.current && !supportRef.current.contains(e.target)) setSupportOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") closeAll();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [closeAll]);

  const displayName = getDisplayName(headerUser);
  const avatarLetter = getAvatarLetter(headerUser);

  const ownerPasswordLabel =
    hasOwnerPassword === true
      ? "Change password"
      : hasOwnerPassword === false
      ? "Set password"
      : "Set / change password";

  const payoutHint = (() => {
    const s = up(payoutSummary?.status);
    if (s === "VERIFIED") return "Verified for payouts";
    if (s === "ACTION_REQUIRED") return "Fix issues to get verified";
    if (s === "SUBMITTED") return "Under review";
    return "Complete setup & submit";
  })();

  return (
    <header
      className={`sticky top-0 z-50 border-b border-slate-200 ${
        mobileOpen ? "bg-white" : "bg-white/90 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-3 sm:h-20 sm:gap-4">
          {/* Logo */}
          <Link to={logoHref} aria-label="Wayroot" className="shrink-0">
            <img
              src="/Wayroot (1600 x 500 px).svg"
              alt="Wayroot"
              className={`w-auto select-none sm:h-12 md:h-14 ${
                isSignedIn ? "h-9" : "h-10"
              }`}
              draggable={false}
            />
          </Link>

          {/* Center nav (desktop) */}
          <nav className="hidden md:flex items-center gap-2" aria-label="Primary">
            {activeNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                <span className="inline-flex items-center gap-2">
                  <span>{item.label}</span>
                  {item.to === "/cart" && role === ROLES.USER && cartPlanCount > 0 ? (
                    <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-slate-900 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                      {cartPlanCount}
                    </span>
                  ) : null}
                </span>
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3">
            {/* Contact us button */}
            <div
              className={`relative ${
                isSignedIn ? "hidden md:block" : "block"
              }`}
              ref={supportRef}
            >
              <button
                type="button"
                aria-label="Contact us"
                onClick={() => setSupportOpen((v) => !v)}
                className="inline-flex h-10 items-center rounded-xl border border-slate-300 bg-white
                           px-3 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition whitespace-nowrap"
              >
                Contact us
              </button>

              {supportOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-48 rounded-xl border border-slate-200 bg-white shadow-lg p-2"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSupportOpen(false);
                      window.location.href = 'tel:9065152425';
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call Us
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSupportOpen(false);
                      window.open('https://wa.me/919065152425', '_blank');
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    WhatsApp
                  </button>
                </div>
              )}
            </div>

            {/* Desktop account / auth */}
            <div className="hidden md:flex items-center">
              {!isSignedIn ? (
                <Link
                  to={signInHref}
                  className="inline-flex h-10 items-center rounded-xl border bg-white px-4 text-sm font-semibold
                             text-[color:var(--brand-pink,#FF2D83)]
                             border-[color:var(--brand-pink,#FF2D83)]
                             hover:bg-[rgba(255,45,131,0.02)] transition"
                >
                  {signInLabel}
                </Link>
              ) : (
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((v) => !v)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white
                               px-3.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 transition"
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-100 text-[12px] font-semibold text-slate-700 border border-slate-300">
                      {avatarLetter}
                    </span>
                    <span className="max-w-[120px] truncate lg:max-w-[160px]">{displayName}</span>
                    <svg
                      className={`h-4 w-4 text-slate-600 transition-transform ${
                        menuOpen ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>

                  {menuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-lg p-2"
                    >
                      {role === ROLES.OWNER && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-500">
                                Account details
                              </div>
                              <div className="mt-1 text-sm font-semibold text-slate-900">
                                Payout account
                              </div>
                              <div className="mt-0.5 text-xs text-slate-600">
                                {payoutLoading
                                  ? "Loading…"
                                  : payoutSummary
                                  ? `Method: ${up(payoutSummary.method || "UPI")}`
                                  : "Set your payout method"}
                              </div>
                            </div>
                            <div className="shrink-0">
                              <StatusChip status={payoutSummary?.status || "DRAFT"} />
                            </div>
                          </div>

                          <div className="mt-2 flex items-center justify-between gap-2">
                            <Link
                              to="/owner/account"
                              onClick={() => setMenuOpen(false)}
                              className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition"
                            >
                              Manage
                            </Link>

                            <span className="text-[11px] text-slate-500">
                              {payoutHint}
                            </span>
                          </div>
                        </div>
                      )}

                      {role === ROLES.USER && (
                        <>
                          <MenuItem to="/user-dashboard" onSelect={() => setMenuOpen(false)}>
                            Dashboard
                          </MenuItem>
                          <MenuItem to="/my-bookings" onSelect={() => setMenuOpen(false)}>
                            My bookings
                          </MenuItem>
                          <MenuItem to="/cart" onSelect={() => setMenuOpen(false)}>
                            My cart
                          </MenuItem>
                          <MenuItem to="/profile" onSelect={() => setMenuOpen(false)}>
                            Profile
                          </MenuItem>
                        </>
                      )}

                      {role === ROLES.OWNER && (
                        <>
                          <MenuItem to="/owner/dashboard" onSelect={() => setMenuOpen(false)}>
                            Owner dashboard
                          </MenuItem>
                          <MenuItem to="/owner/cars" onSelect={() => setMenuOpen(false)}>
                            Manage cars
                          </MenuItem>
                          <MenuItem to="/owner/bookings" onSelect={() => setMenuOpen(false)}>
                            Manage bookings
                          </MenuItem>
                          <MenuItem to="/owner/drivers" onSelect={() => setMenuOpen(false)}>
                            Manage drivers
                          </MenuItem>
                          <MenuItem to="/owner/earnings" onSelect={() => setMenuOpen(false)}>
                            Earnings
                          </MenuItem>
                          <MenuItem to="/owner/profile" onSelect={() => setMenuOpen(false)}>
                            Owner profile
                          </MenuItem>
                          <MenuItem to="/owner/security" onSelect={() => setMenuOpen(false)}>
                            {ownerPasswordLabel}
                          </MenuItem>
                        </>
                      )}

                      {role === ROLES.DRIVER && (
                        <>
                          <MenuItem to="/driver/dashboard" onSelect={() => setMenuOpen(false)}>
                            Driver dashboard
                          </MenuItem>
                          <MenuItem to="/driver/jobs" onSelect={() => setMenuOpen(false)}>
                            Jobs
                          </MenuItem>
                          <MenuItem to="/driver/schedule" onSelect={() => setMenuOpen(false)}>
                            Schedule
                          </MenuItem>
                          <MenuItem to="/driver/earnings" onSelect={() => setMenuOpen(false)}>
                            Earnings
                          </MenuItem>
                        </>
                      )}

                      <div className="my-1 border-t border-slate-200" />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={signOutNow}
                        className="w-full text-left rounded-lg px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isSignedIn && role === ROLES.USER && (
              <div className="flex items-center gap-2 md:hidden">
                <Link
                  to="/my-bookings"
                  aria-label="Bookings"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white shadow-sm hover:bg-slate-50"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5 text-slate-700"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
                    <path d="M7 3.5v3M17 3.5v3M3.5 9.5h17" />
                  </svg>
                </Link>

                <Link
                  to="/cart"
                  aria-label="Cart"
                  className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white shadow-sm hover:bg-slate-50"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5 text-slate-700"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="19" r="1.5" />
                    <circle cx="17" cy="19" r="1.5" />
                    <path d="M4 5h2l1.6 8.2a1 1 0 0 0 1 .8h7.8a1 1 0 0 0 1-.8L19 8H7.2" />
                  </svg>
                  {cartPlanCount > 0 ? (
                    <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-slate-900 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
                      {cartPlanCount}
                    </span>
                  ) : null}
                </Link>
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              type="button"
              aria-label="Open menu"
              className="inline-flex md:hidden items-center justify-center h-10 w-10 rounded-full border border-slate-300 bg-white shadow-sm hover:bg-black/[0.05]"
              onClick={() => setMobileOpen(true)}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 text-slate-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Brand rail */}
      <div className="h-[3px] w-full" style={{ background: "var(--brand-gradient)" }} />

      {/* ✅ Mobile drawer: FULL-WIDTH overlay + centered panel */}
      {mobileOpen && (
        <>
          {/* Overlay */}
          <button
            aria-label="Close menu"
            className="fixed inset-0 z-[70] bg-slate-950/45 md:hidden"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer shell (full screen) */}
          <div
            className="fixed inset-0 z-[80] md:hidden"
            role="dialog"
            aria-modal="true"
          >
            {/* Panel: always full-width on mobile/tablet until desktop nav takes over */}
            <div className="absolute inset-0 bg-white shadow-2xl">
              <div className="flex h-full w-full flex-col bg-white">
                {/* Safe area padding for notch devices */}
                <div className="pt-[env(safe-area-inset-top)]" />

                <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                  <Link to={logoHref} onClick={() => setMobileOpen(false)} className="shrink-0">
                    <img
                      src="/Wayroot (1600 x 500 px).svg"
                      alt="Wayroot"
                      className="h-11 w-auto"
                      draggable={false}
                    />
                  </Link>
                  <button
                    type="button"
                    aria-label="Close"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-4 w-4 text-slate-700"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 6l12 12M18 6l-12 12" />
                    </svg>
                  </button>
                </div>

                {isSignedIn && (
                  <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-4">
                    <span className="grid h-11 w-11 place-items-center rounded-full border border-slate-300 bg-slate-100 text-sm font-semibold text-slate-700">
                      {avatarLetter}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-base font-semibold text-slate-800">
                        {displayName}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto overscroll-contain bg-white">
                {/* Primary links */}
                <nav className="bg-white px-4 py-4" aria-label="Mobile primary">
                  {!isSignedIn ? (
                    <NavLinkLike to={signInHref} onClick={() => setMobileOpen(false)}>
                      {signInLabel}
                    </NavLinkLike>
                  ) : null}
                  {isSignedIn && (
                    <NavLinkLike to={logoHref} onClick={() => setMobileOpen(false)}>
                      Dashboard
                    </NavLinkLike>
                  )}
                  {activeNav.map((item) => (
                    <NavLinkLike key={item.to} to={item.to} onClick={() => setMobileOpen(false)}>
                      <span className="inline-flex items-center gap-2">
                        <span>{item.label}</span>
                        {item.to === "/cart" && role === ROLES.USER && cartPlanCount > 0 ? (
                          <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-slate-900 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                            {cartPlanCount}
                          </span>
                        ) : null}
                      </span>
                    </NavLinkLike>
                  ))}
                </nav>

                {/* Account actions */}
                <div className="space-y-2 border-t border-slate-200 bg-white px-4 py-4">
                  {/* Support button */}
                  <div className="pb-2">
                    <div className="mb-2 text-sm font-semibold text-slate-900">Contact us</div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setMobileOpen(false);
                          window.location.href = 'tel:9065152425';
                        }}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Call
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileOpen(false);
                          window.open('https://wa.me/919065152425', '_blank');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-green-300 bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-100"
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        WhatsApp
                      </button>
                    </div>
                  </div>

                  {isSignedIn ? (
                    <>
                      {role === ROLES.OWNER && (
                        <>
                          <NavLinkLike to="/owner/account" onClick={() => setMobileOpen(false)}>
                            Payout account
                          </NavLinkLike>
                          <NavLinkLike to="/owner/profile" onClick={() => setMobileOpen(false)}>
                            Owner profile
                          </NavLinkLike>
                          <NavLinkLike to="/owner/security" onClick={() => setMobileOpen(false)}>
                            {ownerPasswordLabel}
                          </NavLinkLike>
                        </>
                      )}

                      <NavLinkLike to="/profile" onClick={() => setMobileOpen(false)}>
                        Profile &amp; addresses
                      </NavLinkLike>

                      <button
                        type="button"
                        onClick={signOutNow}
                        className="block min-h-[52px] w-full rounded-2xl px-4 py-3 text-left text-base font-semibold text-rose-600 hover:bg-rose-50"
                      >
                        Sign out
                      </button>
                    </>
                  ) : null}
                </div>
                </div>

                <div className="pb-[env(safe-area-inset-bottom)]" />
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}

function MenuItem({ to, children, onSelect }) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onSelect}
      className="block rounded-lg px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
    >
      {children}
    </Link>
  );
}

function NavLinkLike({ to, children, onClick }) {
  const { pathname } = useLocation();
  const isActive = pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block min-h-[52px] rounded-2xl px-4 py-3.5 text-base font-semibold ${
        isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </Link>
  );
}
